import React, { useEffect, useMemo, useRef, useState } from "react";
import { logger } from "../../shared/logger";

/* ---------------- Price bubble SVG (Airbnb-ish) ---------------- */
function makePriceMarkerIcon(priceText, opts = {}) {
  const {
    fontSize = 13,
    fontWeight = 700,
    paddingX = 10,
    paddingY = 6,
    radius = 12,
    tailWidth = 16,
    tailHeight = 10,
    stroke = "#cccccc",
    strokeWidth = 1,
    fill = "#ffffff",
    textColor = "#111827",
    dsDx = 0,
    dsDy = 1.25,
    dsBlur = 2,
    dsOpacity = 0.25,
    minWidth = 54,
  } = opts;

  const label = String(priceText || "").trim() || "—";
  const textW = Math.max(minWidth, Math.round(label.length * (fontSize * 0.62)) + paddingX * 2);
  const bubbleH = fontSize + paddingY * 2;
  const w = textW;
  const h = bubbleH + tailHeight;

  const cx = w / 2;
  const tailTopY = bubbleH - 0.5;
  const tailLeftX = cx - tailWidth / 2;
  const tailRightX = cx + tailWidth / 2;
  const tailTipY = bubbleH + tailHeight;

  const halfStroke = strokeWidth / 2;
  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
  <defs>
    <filter id="nxw_ds" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="${dsDx}" dy="${dsDy}" stdDeviation="${dsBlur}" flood-opacity="${dsOpacity}"/>
    </filter>
  </defs>
  <g filter="url(#nxw_ds)">
    <rect x="${halfStroke}" y="${halfStroke}" width="${w - strokeWidth}" height="${bubbleH - strokeWidth}"
          rx="${radius}" ry="${radius}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/>
    <path d="M ${tailLeftX} ${tailTopY} L ${cx} ${tailTipY} L ${tailRightX} ${tailTopY} Z"
          fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/>
  </g>
  <text x="${cx}" y="${bubbleH / 2 + 1}"
        font-family="system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif"
        font-size="${fontSize}" font-weight="${fontWeight}"
        fill="${textColor}" text-anchor="middle" dominant-baseline="middle">${label}</text>
</svg>`.trim();

  return {
    url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
    scaledSize: new window.google.maps.Size(w, h),
    anchor: new window.google.maps.Point(cx, h),
  };
}

/* ---------------- Utilities ---------------- */
function httpsify(u) {
  return typeof u === "string" ? u.replace(/^http:\/\//i, "https://") : "";
}
function plural(n, one, many) {
  return `${n} ${n === 1 ? one : many}`;
}
function escHtml(s = "") {
  return String(s)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}
function escAttr(s = "") {
  return String(s).replace(/"/g, "&quot;").replace(/'/g, "&#039;");
}

/* Clean, Leaflet-like popup styling */
function ensurePopupCss() {
  if (document.getElementById("__nx_gmap_popup_css")) return;
  const s = document.createElement("style");
  s.id = "__nx_gmap_popup_css";
  s.textContent = `
/* remove gutters/tail, keep only our card */
.gm-style .gm-style-iw { padding:0!important; }
.gm-style .gm-style-iw-d { overflow:visible!important; }
.gm-style .gm-style-iw-tc::after { display:none!important; }
.gm-style .gm-style-iw-c { padding:0!important; margin:0!important; }
.gm-style .gm-style-iw-chr { display:none!important; }
.gm-style .gm-style-iw-t { margin:0!important; }

/* Remove gap at top of info window */
.gm-style-iw-d > div { margin:0!important; padding:0!important; }
.gm-style-iw.gm-style-iw-c {
  padding:0!important;
  max-width: none!important;
}

/* close button */
.gm-ui-hover-effect {
  top:8px!important; right:8px!important;
  width:32px!important; height:32px!important;
  border-radius:10px!important;
  background:rgba(255,255,255,.9)!important;
  box-shadow:0 1px 2px rgba(0,0,0,.08);
  border:1px solid rgba(17,24,39,.12);
}
.gm-ui-hover-effect span { margin:6px!important; }

/* our card */
.nx-card{width:340px;max-width:85vw;
  font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;
  color:#111827;background:#fff;border-radius:12px;overflow:hidden;
  border:1px solid rgba(17,24,39,.12);box-shadow:0 14px 34px rgba(2,6,23,.28);
}
.nx-card-img{display:block;width:100%;aspect-ratio:16/9;object-fit:cover;background:#f3f4f6;}
.nx-card-body{padding:12px 14px 14px;}
.nx-card-title{font-weight:800;font-size:18px;margin:0 0 6px;line-height:1.2;}
.nx-card-meta{font-size:12px;letter-spacing:.2px;color:#6b7280;display:flex;gap:8px;flex-wrap:wrap;}
.nx-dot::before{content:"·";margin:0 6px;color:#9ca3af;}
.nx-card a{color:inherit;text-decoration:none;}
.nx-card a:hover .nx-card-title{text-decoration:underline;}
`;
  document.head.appendChild(s);
}

function buildInfoHtml(it) {
  const t = escHtml(it?.title || "");
  const rawImg = it?.image || (Array.isArray(it?.images) ? it.images.find(Boolean) : "");
  const img = httpsify(rawImg || "");
  const href = it?.href ? escAttr(it.href) : "";

  const metaBits = [];
  if (Number.isFinite(it?.guests)) metaBits.push(plural(it.guests, "Guest", "Guests"));
  if (Number.isFinite(it?.bedrooms)) metaBits.push(plural(it.bedrooms, "Bedroom", "Bedrooms"));
  if (Number.isFinite(it?.bathrooms)) metaBits.push(plural(it.bathrooms, "Bathroom", "Bathrooms"));
  const meta = metaBits.join(' <span class="nx-dot"></span> ');

  const inner = `
    <div class="nx-card">
      ${img ? `<img class="nx-card-img" src="${escAttr(img)}" alt="${t}"/>`
            : `<div class="nx-card-img" role="presentation"></div>`}
      <div class="nx-card-body">
        <h3 class="nx-card-title">${t}</h3>
        <div class="nx-card-meta">${meta}</div>
      </div>
    </div>
  `;
  return href
    ? `<a href="${href}" target="_blank" rel="noopener noreferrer">${inner}</a>`
    : inner;
}

export default function GoogleMapView({
  items = [],
  height = 520,
  activeId = null,
  visible = true,
  apiKey = "",
  iconSpec = {
    mode: "fa",
    faFamily: "fa-solid",
    faIcon: "fa-location-dot",
    size: 28,
    color: "#fe5922",
    svg: "",
  },
  isPricePins = false,
  enableClustering = false,
  clusterStyle = {
    bgColor: "#fe5922",
    textColor: "#ffffff",
    borderColor: "#ffffff",
    borderWidth: 3,
    fontSize: 14,
    fontWeight: 700,
    size: 40,
  },
  customSvgUrl = "",
  onPinClick,
  onBoundsChange,
  mapType = "roadmap", // roadmap | satellite | hybrid | terrain
}) {
  const containerRef = useRef(null);
  const map = useRef(null);
  const infoWin = useRef(null);
  const markers = useRef(new Map()); // id -> google.maps.Marker
  const markerClusterer = useRef(null); // MarkerClusterer instance
  const boundsSet = useRef(false); // Track if initial bounds have been set
  const [scriptReady, setScriptReady] = useState(false);
  const [clustererReady, setClustererReady] = useState(false);

  /* --------------- Resolve marker icon --------------- */
  const defaultPinIcon = useMemo(() => {
    const size = Number.isFinite(iconSpec?.size) ? iconSpec.size : 28;
    const color = iconSpec?.color || "#fe5922";
    // nice vector path pin
    return {
      path: "M12 2C7.58 2 4 5.58 4 10c0 5.25 7.2 12 8 12s8-6.75 8-12c0-4.42-3.58-8-8-8zm0 11.5c-1.93 0-3.5-1.57-3.5-3.5S10.07 6.5 12 6.5s3.5 1.57 3.5 3.5S13.93 13.5 12 13.5z",
      fillColor: color,
      fillOpacity: 1,
      strokeColor: "#000000",
      strokeOpacity: 0.15,
      strokeWeight: 1,
      scale: size / 24,
      anchor: window.google ? new window.google.maps.Point(12, 24) : undefined,
    };
  }, [iconSpec]);

  const customSvgIcon = useMemo(() => {
    if (!customSvgUrl) return null;
    const sz = Number.isFinite(iconSpec?.size) ? iconSpec.size : 28;
    if (!window.google?.maps) return null;
    return {
      url: customSvgUrl,
      scaledSize: new window.google.maps.Size(sz, sz),
      anchor: new window.google.maps.Point(Math.round(sz / 2), sz),
    };
  }, [customSvgUrl, iconSpec]);

  /* --------------- Load Google Maps script --------------- */
  useEffect(() => {
    if (!visible || !apiKey) return;

    ensurePopupCss();

    // If script already present
    const existing = document.querySelector(
      `script[src*="maps.googleapis.com/maps/api/js?key="]`
    );
    if (existing) {
      if (window.google?.maps) setScriptReady(true);
      else existing.addEventListener("load", () => setScriptReady(true), { once: true });
      return;
    }

    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    s.async = true;
    s.defer = true;
    s.onload = () => setScriptReady(true);
    document.head.appendChild(s);
  }, [apiKey, visible]);

  /* --------------- Load MarkerClusterer library --------------- */
  useEffect(() => {
    if (!enableClustering) {
      setClustererReady(false);
      return;
    }

    // Check if already loaded
    if (window.markerClusterer || window.MarkerClusterer) {
      setClustererReady(true);
      return;
    }

    // Load from CDN
    if (!document.getElementById("__google_clusterer_js")) {
      const script = document.createElement("script");
      script.id = "__google_clusterer_js";
      script.src = "https://unpkg.com/@googlemaps/markerclusterer@2.5.3/dist/index.min.js";
      script.onload = () => {
        setClustererReady(true);
      };
      script.onerror = () => {
        logger.warn("[GoogleMapView] Failed to load MarkerClusterer");
        setClustererReady(false);
      };
      document.head.appendChild(script);
    }
  }, [enableClustering]);

  /* --------------- Init map --------------- */
  useEffect(() => {
    if (!scriptReady || !visible) return;
    if (!containerRef.current || map.current) return;

    map.current = new window.google.maps.Map(containerRef.current, {
      center: { lat: 51.509865, lng: -0.118092 },
      zoom: 6,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      gestureHandling: "greedy",
    });

    infoWin.current = new window.google.maps.InfoWindow({
      content: "",
      maxWidth: 380,
      pixelOffset: new window.google.maps.Size(0, -8),
      disableAutoPan: false,
    });
  }, [scriptReady, visible]);

  /* --------------- Update map type when prop changes --------------- */
  useEffect(() => {
    const m = map.current;
    if (!m || !window.google?.maps) return;

    try {
      m.setMapTypeId(mapType);
    } catch (e) {
      logger.warn("[GoogleMapView] Failed to set map type:", e);
    }
  }, [mapType]);

  /* --------------- Listen for map bounds changes (pan/zoom) --------------- */
  useEffect(() => {
    const m = map.current;
    if (!m || !onBoundsChange || !window.google?.maps) return;

    const emitBounds = () => {
      try {
        const bounds = m.getBounds();
        if (bounds) {
          const ne = bounds.getNorthEast();
          const sw = bounds.getSouthWest();
          onBoundsChange({
            north: ne.lat(),
            south: sw.lat(),
            east: ne.lng(),
            west: sw.lng(),
          });
        }
      } catch (e) {
        logger.warn("[GoogleMapView] Failed to get bounds:", e);
      }
    };

    // Emit initial bounds after a short delay (map needs to be ready)
    const initTimer = setTimeout(emitBounds, 500);

    // Listen for 'idle' event (fires after pan/zoom is complete)
    const listener = m.addListener("idle", emitBounds);

    return () => {
      clearTimeout(initTimer);
      window.google.maps.event.removeListener(listener);
    };
  }, [onBoundsChange, scriptReady]);

  /* --------------- Place / update markers --------------- */
  // Memoize items by their IDs to prevent unnecessary re-renders
  const itemsKey = useMemo(() => {
    if (!items?.length) return 'empty';
    return items.map(it => it?.id ?? '').join(',');
  }, [items]);

  useEffect(() => {
    if (!map.current || !window.google?.maps) return;

    // Wait for clusterer if enabled
    if (enableClustering && !clustererReady) return;

    // clear previous clusterer
    if (markerClusterer.current) {
      markerClusterer.current.clearMarkers();
      markerClusterer.current.setMap(null);
      markerClusterer.current = null;
    }

    // clear previous markers
    markers.current.forEach((m) => m.setMap(null));
    markers.current.clear();

    const g = window.google.maps;
    const bounds = new g.LatLngBounds();

    const points = (items || []).filter(
      (it) => Number.isFinite(it?.lat) && Number.isFinite(it?.lng)
    );

    const markerArray = [];

    points.forEach((it, idx) => {
      const id = it?.id ?? idx;
      let icon;

      if (isPricePins) {
        icon = makePriceMarkerIcon(it.price || "");
      } else if (customSvgIcon) {
        icon = customSvgIcon;
      } else {
        icon = defaultPinIcon;
      }

      const marker = new g.Marker({
        position: { lat: it.lat, lng: it.lng },
        map: enableClustering ? null : map.current, // Don't add to map if clustering
        icon,
        title: String(it?.title || ""),
      });

      // stash full item for popup
      marker.__nx_item = it;

      marker.addListener("click", () => {
        // build & inject content as a DOM node
        const div = document.createElement("div");
        div.innerHTML = buildInfoHtml(it);
        infoWin.current.setContent(div);
        infoWin.current.open({ map: map.current, anchor: marker, shouldFocus: false });

        // nudge after image load to keep bubble well placed
        setTimeout(() => {
          const img = div.querySelector?.("img.nx-card-img");
          if (!img) return;
          img.addEventListener(
            "load",
            () => {
              try { map.current?.panBy(0, 0); } catch {}
            },
            { once: true }
          );
        }, 0);

        onPinClick?.(id, it);
      });

      markers.current.set(id, marker);
      markerArray.push(marker);
      bounds.extend(marker.getPosition());
    });

    // Add markers to clusterer or map
    if (enableClustering && clustererReady && window.markerClusterer) {
      try {
        const size = clusterStyle.size || 40;
        const bgColor = clusterStyle.bgColor || "#ffffff";
        const textColor = clusterStyle.textColor || "#222222";
        const borderColor = clusterStyle.borderColor || "#cccccc";
        const borderWidth = clusterStyle.borderWidth || 1;
        const fontSize = clusterStyle.fontSize || 14;
        const fontWeight = clusterStyle.fontWeight || 700;

        markerClusterer.current = new window.markerClusterer.MarkerClusterer({
          map: map.current,
          markers: markerArray,
          renderer: {
            render: ({ count, position }) => {
              const svg = `<svg width="${size}" height="${size}" xmlns="http://www.w3.org/2000/svg">
                <circle cx="${size/2}" cy="${size/2}" r="${size/2 - 1}"
                  fill="${bgColor}" stroke="${borderColor}" stroke-width="${borderWidth}"/>
                <text x="${size/2}" y="${size/2}"
                  text-anchor="middle" dominant-baseline="central"
                  font-size="${fontSize}" font-weight="${fontWeight}"
                  font-family="system-ui,-apple-system,sans-serif"
                  fill="${textColor}">${count}</text>
              </svg>`;

              return new google.maps.Marker({
                position,
                icon: {
                  url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
                  scaledSize: new google.maps.Size(size, size),
                  anchor: new google.maps.Point(size/2, size/2),
                },
                zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
              });
            },
          },
        });
      } catch (err) {
        logger.warn("[GoogleMapView] Failed to create MarkerClusterer:", err);
        // Fallback: add markers directly to map
        markerArray.forEach(m => m.setMap(map.current));
      }
    }

    // Only fit bounds on first load, preserve user's zoom/pan after that
    if (points.length && !boundsSet.current) {
      try {
        map.current.fitBounds(bounds, { top: 40, right: 40, bottom: 40, left: 40 });
        boundsSet.current = true;
      } catch {}
    }

    return () => {
      if (markerClusterer.current) {
        markerClusterer.current.clearMarkers();
        markerClusterer.current.setMap(null);
        markerClusterer.current = null;
      }
      markers.current.forEach((m) => m.setMap(null));
      markers.current.clear();
    };
  }, [itemsKey, items, isPricePins, customSvgIcon, defaultPinIcon, onPinClick, enableClustering, clustererReady, clusterStyle]);

  /* --------------- Highlight active marker + open popup --------------- */
  useEffect(() => {
    if (!map.current || !window.google?.maps) return;

    markers.current.forEach((marker, id) => {
      const isActive = String(id) === String(activeId);
      if (isActive) {
        try {
          marker.setZIndex(window.google.maps.Marker.MAX_ZINDEX + 1);
          marker.setAnimation(window.google.maps.Animation.BOUNCE);
          setTimeout(() => marker.setAnimation(null), 600);

          const it = marker.__nx_item || { title: marker.getTitle?.() || "" };
          const div = document.createElement("div");
          div.innerHTML = buildInfoHtml(it);
          infoWin.current.setContent(div);
          infoWin.current.open({ map: map.current, anchor: marker, shouldFocus: false });
        } catch {}
      } else {
        try {
          marker.setZIndex(undefined);
          marker.setAnimation(null);
        } catch {}
      }
    });
  }, [activeId]);

  /* --------------- Render --------------- */
  return (
    <div
      ref={containerRef}
      className="nxm-google-map"
      style={{
        height,
        width: "100%",
        borderRadius: 12,
        overflow: "hidden",
        display: visible ? "block" : "none",
        border: "1px solid rgba(17,24,39,.12)",
      }}
    />
  );
}
